import * as Bowser from 'bowser';
import { UAParser } from 'ua-parser-js';
import http from './http';

const USER_DEVICE_TYPE = {
  PHONE: 'smartphone',
  TABLET: 'tablet',
  COMPUTER: 'desktop',
};

const DEVICE_NAME_MAP = {
  [USER_DEVICE_TYPE.PHONE]: 'phone',
  [USER_DEVICE_TYPE.TABLET]: 'tablet',
  [USER_DEVICE_TYPE.COMPUTER]: 'computer',
};

const DEVICE_TYPE_MAP = {
  mobile: USER_DEVICE_TYPE.PHONE,
  tablet: USER_DEVICE_TYPE.TABLET,
  desktop: USER_DEVICE_TYPE.COMPUTER,
};

const deviceNameMap = {
  smartphone: 'phone',
  tablet: 'tablet',
  desktop: 'computer',
};
const DEFAULT_GENERIC_NAME = 'device';
const ZEUS_API = process.env.ZEUS_API;
const LS_KEY_DEVICE = 'samknows-iframe-detected-device';
const DEVICE_DETECTION_VERSION = '2';

export default {
  getDeviceFromUserAgent() {
    const parser = new UAParser();
    const device = parser.getDevice();

    const browser = Bowser.parse(window.navigator.userAgent);
    const deviceTypeKey = device.type || browser.platform.type;

    const platformType = DEVICE_TYPE_MAP[deviceTypeKey];

    return {
      ...device,
      // We can't have manufacturer from User Agent
      manufacturer: device.vendor,
      name: DEVICE_NAME_MAP[device.type],
      type: platformType,
      isMobileDevice: [
        USER_DEVICE_TYPE.PHONE,
        USER_DEVICE_TYPE.TABLET,
      ].indexOf(platformType) > -1,
      genericName: deviceNameMap[device.type] || DEFAULT_GENERIC_NAME,
    };
  },

  waitForDeviceAtlasCookie() {
    let intervalId = null;

    return new Promise((resolve) => {
      // Wait 3 seconds for cookie to be set
      setTimeout(() => {
        clearInterval(intervalId);
        resolve('');
      }, 3000);

      intervalId = setInterval(() => {
        const cookieName = 'DAPROPS';
        const deviceAtlasCookie = document.cookie.match(
          `(^|;)\\s*${cookieName}\\s*=\\s*([^;]+)`
        );

        if (!deviceAtlasCookie) {
          return;
        }
        resolve(deviceAtlasCookie[2]);
        clearInterval(intervalId);
      }, 100);
    });
  },

  async getDeviceFromApi() {
    const cookie = await this.waitForDeviceAtlasCookie();
    const { data } = await http.request(`${ZEUS_API}/device-detect`, {
      method: 'POST',
      body: JSON.stringify({
        device_cookie_data: cookie,
      }),
    });
    const properties = data.properties;
    let type = properties.primary_hardware_type.toLowerCase().replace(' ', '_');
    if (type === 'mobile_phone') {
      type = USER_DEVICE_TYPE.PHONE;
    }
    const device = {
      type,
      isMobileDevice: properties.is_mobile_phone || properties.is_tablet,
      model: properties.model,
      name: properties.marketing_name,
      vendor: properties.vendor,
      manufacturer: properties.manufacturer,
      genericName: '',
    };
    device.genericName = deviceNameMap[device.type] || DEFAULT_GENERIC_NAME;

    return device;
  },

  isLocalStorageSupported() {
    try {
      const testKey = 'false_key';
      window.localStorage.setItem(testKey, testKey);
      window.localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  },

  /**
   * Return cached device saved in local storage
   * The device is
   * - cached for 30 days: cached device will be deleted when expired to force device detection.
   * - versioned: cached device will be deleted if version is below current version,
   * to force device detection.
   * @returns device
   */
  getSavedDevice() {
    const savedDeviceString = localStorage.getItem(LS_KEY_DEVICE);

    if (!savedDeviceString) {
      return undefined;
    }

    const savedDevice = JSON.parse(savedDeviceString);

    const isOutdatedVersion = savedDevice.version !== DEVICE_DETECTION_VERSION;
    const hasExpired = Date.now() > savedDevice.expiry;

    if (isOutdatedVersion || hasExpired) {
      localStorage.removeItem(LS_KEY_DEVICE);
      return undefined;
    }

    return savedDevice.value;
  },

  /**
   * Detect device used by user
   * - Check cached device first
   * - Get device from API if no device was cached and save for next use: expire after 30 days
   * - Get device from user agent as last resort if API detection didn't work
   * @returns device
   */
  async getDevice() {
    let device = this.isLocalStorageSupported() ? this.getSavedDevice() : undefined;
    if (device) {
      return device;
    }
    try {
      device = await this.getDeviceFromApi();

      // 30 days in milliseconds
      const ttl = 30 * 24 * 60 * 60 * 1000;
      const deviceToSave = {
        value: device,
        expiry: Date.now() + ttl,
        version: DEVICE_DETECTION_VERSION,
      };

      if (this.isLocalStorageSupported()) {
        localStorage.setItem(LS_KEY_DEVICE, JSON.stringify(deviceToSave));
      }
    } catch (e) {
      device = this.getDeviceFromUserAgent();
    }

    return device;
  },
};



// WEBPACK FOOTER //
// ./src/js/lib/detect-device.js